import { useEffect, useState } from 'react'
import { useAuthContext } from '../../context/auth.context'
import { getCatalogue } from './audit-api'
import { identityKey } from './use-audit-capabilities'

// The audit catalogue (GET /catalogue): categories, actions and record
// types with their labels -- data the screens render, never strings built
// from identifiers. It differs only between super admins and everyone
// else and changes only with a deploy, so it is read once per identity
// (an impersonated tab sees the customer's catalogue). null while loading;
// a failed read gives an empty catalogue and is tried again on the next
// mount.
const EMPTY = { categories: [], actions: [], tables: [] }
const cache = new Map()

export const resetAuditCatalogue = () => cache.clear()

export const useAuditCatalogue = () => {
  const { state } = useAuthContext()
  const key = identityKey(state)
  const cached = cache.get(key)
  const [catalogue, setCatalogue] = useState(() => (cached && cached.value) || null)

  useEffect(() => {
    let live = true
    if (!cache.has(key)) {
      const entry = {}
      entry.promise = getCatalogue().then(
        v => {
          entry.value = v || EMPTY
          return entry.value
        },
        () => {
          if (cache.get(key) === entry) cache.delete(key)
          return EMPTY
        }
      )
      cache.set(key, entry)
    }
    cache.get(key).promise.then(v => {
      if (live) setCatalogue(v)
    })
    return () => {
      live = false
    }
  }, [key])

  return catalogue
}

// A record type's table in the catalogue. A record type is the type the
// trail records a table's rows as (content's recordType: alert_routes rows
// are alert_route records), which is not always the table's name; an older
// content without recordType names the table.
export const catalogueTableOf = (catalogue, type) =>
  ((catalogue && catalogue.tables) || []).find(t => (t.recordType || t.table) === type) || null

// The labels of a record type's child tables ("Include its asset versions"):
// a child names its parent by the parent's table.
export const childLabelsOf = (catalogue, type) => {
  const own = catalogueTableOf(catalogue, type)
  const parent = own ? own.table : type
  return ((catalogue && catalogue.tables) || []).filter(t => t.parent === parent).map(t => t.label)
}
